import * as httpStatusCodes from 'http-status-codes'
import uniqueId from 'lodash/uniqueId'
import { all, call, put, SagaReturnType, select, takeEvery, takeLatest } from 'redux-saga/effects'

import { DisplayOption } from '../../../schema/webshop-package'
import getProducts from '../../../service/products/getProducts'
import addProductToBasket from '../../../service/webshop/add-product-to-basket/addProductToBasket'
import getBasket from '../../../service/webshop/get-basket/getBasket'
import removeProductFromBasket from '../../../service/webshop/remove-product-from-basket/removeProductFromBasket'
import { buildBasketProducts } from '../../../util/build-basket/build-basket'
import { getCorrelationIdFromError } from '../../../util/error/getCorrelationIdFromError'
import navigateToPage from '../../../util/navigate-to-page/navigateToPage'
import {
  unauthorizedWebshopError,
  unexpectedError,
  unexpectedResponse,
} from '../../../util/show-error-dialog/showErrorDialog'
import { addToaster, showDialog } from '../../actions/ui/ui'
import type {
  AddProductToBasketAction,
  RebuildWebshopAction,
  RemoveProductFromBasketAction,
} from '../../actions/webshop/webshop'
import {
  addProductToBasketFail,
  addProductToBasketSuccess,
  ADD_PRODUCT,
  FETCH_PACKAGES,
  getBasket as getBasketAction,
  getBasketSucces,
  GET_BASKET,
  REBUILD_WEBSHOP,
  removeProductFromBasketSuccess,
  REMOVE_PRODUCT_FROM_BASKET,
  setBasketProducts,
  setPackages,
  setHiddenPackages,
} from '../../actions/webshop/webshop'
import { selectCart } from '../../selectors/cart/cart'
import { Basket } from '../../../schema/basket'

export function* fetchPackagesSaga() {
  try {
    const response: SagaReturnType<typeof getProducts> = yield call(getProducts, [DisplayOption.webshop_and_cv_database, DisplayOption.combined, DisplayOption.webshop])
    yield put(setPackages(response.data))

    const hiddenProductsResponse: SagaReturnType<typeof getProducts> = yield call(getProducts, [DisplayOption.hidden])
    yield put(setHiddenPackages(hiddenProductsResponse.data))
  } catch (e) {
    const correlationId = getCorrelationIdFromError(e)

    yield put(
      showDialog({
        title: 'Er ging iets mis',
        content: 'Kan producten niet laden. Laad de pagina opnieuw',
        correlationId,
      }),
    )
  }
}

export function* addProductToBasketSaga(action: AddProductToBasketAction) {
  const toastId = uniqueId()
  try {
    const response = yield call(addProductToBasket, action.payload)
    if (response.status === httpStatusCodes.OK) {
      yield put(
        addToaster({
          id: toastId,
          variant: 'success',
          title: 'Toegevoegd aan winkelwagen',
          description: `We hebben ${action.payload.name} toegevoegd aan je winkelwagen`,
        }),
      )
      yield put(getBasketAction())
      yield put(addProductToBasketSuccess(action.payload))
    } else {
      yield put(unexpectedResponse())
    }
  } catch (error) {
    yield put(addProductToBasketFail())
    if (error.response?.status === httpStatusCodes.UNAUTHORIZED) {
      yield put(unauthorizedWebshopError(action.payload.name))
    } else {
      // yield call(Sentry.captureException, error)
      yield put(
        addToaster({
          id: toastId,
          variant: 'error',
          title: 'Fout bij toevoegen aan winkelwagen',
          description: `We konden ${action.payload.name} niet aan de winkelwagen toevoegen, probeer het alstublieft opnieuw`,
        }),
      )
    }
  }
}

export function* removeProductFromBasketSaga(action: RemoveProductFromBasketAction) {
  try {
    yield call(removeProductFromBasket, action.payload)
    yield put(removeProductFromBasketSuccess())
  } catch (error) {
    // yield call(Sentry.captureException, error)
    yield put(
      showDialog({
        title: 'Kon product niet verwijderen',
        // eslint-disable-next-line max-len
        content: `${action.payload.name} kon helaas niet verwijderd worden uit de winkelwagen. Herlaad de pagina en probeer het opnieuw.`,
        correlationId: getCorrelationIdFromError(error),
      }),
    )
  } finally {
    yield put(getBasketAction())
  }
}

export function* getBasketSaga() {
  try {
    const { data: { items, vatTotal } }: { data: Basket } = yield call(getBasket)
    yield put(getBasketSucces(items, vatTotal))
  } catch (error) {
    yield put(
      showDialog({
        title: 'Kon winkelwagen niet ophalen',
        content: 'Herlaad de pagina en probeer het opnieuw.',
        correlationId: getCorrelationIdFromError(error),
      }),
    )
  }
}

export function* rebuildWebshopSaga(action: RebuildWebshopAction) {
  const { productCodes, paymentAction } = action.payload

  try {
    const response: SagaReturnType<typeof getProducts> = yield call(getProducts, [DisplayOption.combined, DisplayOption.webshop])

    if (response.status === httpStatusCodes.OK && response.data) {
      const basketProducts = buildBasketProducts(productCodes, response.data)
      yield put(setBasketProducts(basketProducts))
      const {
        payment: { status },
      } = yield select(selectCart)

      if (paymentAction) {
        yield put(paymentAction)
      }

      yield call(navigateToPage, `/webshop/cart/${status}`, undefined, { shallow: true })
    } else {
      yield put(unexpectedResponse())
    }
  } catch (error) {
    // yield call(Sentry.captureException, error)
    yield put(unexpectedError(error))
  }
}

export default function* saga() {
  yield all([
    takeLatest(FETCH_PACKAGES, fetchPackagesSaga),
    takeEvery(ADD_PRODUCT, addProductToBasketSaga),
    takeLatest(GET_BASKET, getBasketSaga),
    takeLatest(REMOVE_PRODUCT_FROM_BASKET, removeProductFromBasketSaga),
    takeLatest(REBUILD_WEBSHOP, rebuildWebshopSaga),
  ])
}
